import React, { useState, useEffect } from 'react';
import api from '../api';

export default function ContextsSection({ token, setError }) {
  const [contexts, setContexts] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // add, edit, delete
  const [selected, setSelected] = useState(null);

  const initialForm = {
    title: '',
    contents: [{ content_type: 'text', content: '' }],
    attachments: [], // existing attachments, pulled from backend
    fileAttachments: [], // new files to upload
  };

  const [formData, setFormData] = useState(initialForm);

  const fetch = async () => {
    try {
      const res = await api.get('/admin/contexts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContexts(res.data.contexts);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch contexts');
    }
  };

  useEffect(() => {
    if (!token) return;
    fetch();
  }, [token]);

  const resetForm = () => {
    setFormData(initialForm);
    setSelected(null);
  };

  const onOpenAdd = () => {
    setModalMode('add');
    resetForm();
    setModalOpen(true);
  };

  const onOpenEdit = (context) => {
    setModalMode('edit');
    setSelected(context);
    setFormData({
      title: context.title || '',
      contents: (context.contents && context.contents.length > 0)
        ? context.contents
        : (context.content ? [{ content_type: 'text', content: context.content }] : [{ content_type: 'text', content: '' }]),
      attachments: context.attachments || [],
      fileAttachments: [],
    });
    setModalOpen(true);
  };

  const onOpenDelete = (context) => {
    setModalMode('delete');
    setSelected(context);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        title: formData.title,
        contents: formData.contents
          .filter((c) => (c.content || '').trim().length > 0)
          .map((c, i) => ({
            content_type: c.content_type || 'text',
            content: c.content,
            position: c.position || i + 1,
          })),
      };

      let contextId;
      if (modalMode === 'edit' && selected) {
        await api.put(`/admin/contexts/${selected.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        contextId = selected.id;
      } else {
        const result = await api.post('/admin/contexts', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        contextId = result.data.id;
      }

      if (formData.fileAttachments.length > 0) {
        const uploadData = new FormData();
        formData.fileAttachments.forEach((file) => uploadData.append('attachments', file));
        await api.post(`/admin/contexts/${contextId}/attachments`, uploadData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      await fetch();
      setModalOpen(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save context');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/contexts/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModalOpen(false);
      resetForm();
      await fetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete context');
    }
  };

  const removeAttachment = async (attachmentId) => {
    try {
      await api.delete(`/admin/contexts/attachments/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove attachment');
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setFormData((prev) => ({
      ...prev,
      fileAttachments: [...prev.fileAttachments, ...files],
    }));
  };

  if (!contexts) return null;

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold mb-0 text-indigo-600">Contexts</h3>
        <button
          className="bg-green-500 text-white px-3 py-1 rounded shadow hover:bg-green-600 transition"
          onClick={onOpenAdd}
        >
          + Add
        </button>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl">
            {modalMode === 'delete' ? (
              <>
                <p className="mb-4">Do you really want to delete <b>{selected?.title}</b>?</p>
                <div className="flex justify-end space-x-2">
                  <button className="px-3 py-1 rounded bg-gray-300" onClick={() => setModalOpen(false)}>Cancel</button>
                  <button className="px-3 py-1 rounded bg-red-500 text-white" onClick={handleDelete}>Delete</button>
                </div>
              </>
            ) : (
              <>
                <h4 className="text-lg font-semibold mb-3">{modalMode === 'edit' ? 'Edit' : 'Add'} Context</h4>

                <div className="space-y-3">
                  <input
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="title"
                    className="border rounded px-2 py-2 w-full"
                  />

                  <div className="space-y-2 border p-3 rounded bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Content blocks</span>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({
                          ...prev,
                          contents: [...(prev.contents || []), { content_type: 'text', content: '' }],
                        }))}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        + Add block
                      </button>
                    </div>

                    {(formData.contents || []).map((block, index) => (
                      <div key={index} className="bg-white border rounded p-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-500">Block {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const next = formData.contents.filter((_, i) => i !== index);
                              setFormData((prev) => ({ ...prev, contents: next }));
                            }}
                            className="text-xs text-red-500 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                        <textarea
                          rows={3}
                          value={block.content}
                          onChange={(e) => {
                            const next = formData.contents.map((item, i) => i === index ? { ...item, content: e.target.value } : item);
                            setFormData((prev) => ({ ...prev, contents: next }));
                          }}
                          className="w-full border rounded px-2"
                          placeholder="Enter block content"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <span className="text-sm font-medium">Attachments (image/pdf)</span>
                    <input type="file" multiple accept="image/*,.pdf" onChange={handleFileChange} />

                    {formData.fileAttachments.length > 0 && (
                      <div className="text-xs text-gray-600">
                        Upload queue: {formData.fileAttachments.map((f) => f.name).join(', ')}
                      </div>
                    )}

                    {formData.attachments.length > 0 && (
                      <div className="space-y-1">
                        {formData.attachments.map((att) => (
                          <div key={att.id} className="flex justify-between items-center text-sm border rounded p-2 bg-white">
                            <a className="text-blue-600" href={att.url} target="_blank" rel="noreferrer">{att.title || att.url}</a>
                            <button className="text-red-500" type="button" onClick={() => removeAttachment(att.id)}>Delete</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-2 mt-4">
                    <button className="px-3 py-1 rounded bg-gray-300" onClick={() => setModalOpen(false)}>Cancel</button>
                    <button className="px-3 py-1 rounded bg-blue-600 text-white" onClick={handleSave}>{modalMode === 'edit' ? 'Update' : 'Add'}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {contexts.map((context) => (
          <div key={context.id} className="p-4 bg-white rounded-lg shadow hover:shadow-lg transition">
            <div className="font-semibold text-lg">{context.title}</div>
            <div className="text-sm text-gray-500 mt-1">{(context.contents && context.contents.length > 0 ? `${context.contents.length} content block(s)` : (context.content ? 'Legacy content' : 'No content'))}</div>
            <div className="text-sm text-gray-500 mt-1">{context.attachments?.length ? `${context.attachments.length} attachment(s)` : 'No attachments'}</div>
            <div className="mt-3 flex justify-end space-x-2">
              <button className="text-blue-500 hover:text-blue-700" onClick={() => onOpenEdit(context)}>Edit</button>
              <button className="text-red-500 hover:text-red-700" onClick={() => onOpenDelete(context)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

